// The privacy policy for the UMaps mobile app, at /mobile-privacy-policy.
//
// Both app stores require a policy at a public URL that loads without a login,
// and Apple additionally links to it from the App Store product page — so this
// is a page strangers and reviewers read, not one users go looking for.
//
// WHY IT IS WRITTEN FROM THE CODE. Every claim below was checked against the
// app and this API before it was written down, because a privacy policy is the
// one document where a plausible sentence is worse than none: the store
// questionnaires are answered from it, and an answer that does not match the
// binary is what gets an app pulled rather than rejected. The specific things
// that were verified, each of which a template would have got wrong:
//
//   · Problem reports carry NO position. `submitReport` in the app builds its
//     context by hand and deliberately leaves the user's location out, matching
//     what the website's own report log records. So the policy can say the
//     phone's location itself never leaves the device.
//   · ⚠️ OUTDOOR AUTO-START (October 2026). Asking for directions with a
//     precise fix while outside every building skips the From prompt: the app
//     picks the nearest outdoor nav node (`nav_outdoors.json`, an id like
//     `o_1784337769335_200`) ON THE PHONE and starts the route there. The fix
//     still never leaves the phone, but the route search it counts (behind the
//     Privacy switch) sends `from: { text: "Outdoors", nodeId }` — a fixed
//     point on our map that says roughly where somebody stood. That is why the
//     Location section says "your phone's location ITSELF" and why the
//     route-search row names the footpath point. Problem reports and route
//     ratings send only the From LABEL, never a node id, so their rows still
//     say no location — re-check that if either payload gains From's node.
//   · Reports expire. `_reports.js` sets a ~2 year TTL and caps the list at
//     5000, so the retention sentence is a fact about the store rather than an
//     intention.
//   · Route ratings are the same shape and the same promise. `_routeRatings.js`
//     sets the same ~2 year TTL at a 20,000 cap (a rating is one tap and is
//     offered on every route, so it is the busier of the two), and
//     `src/api/routeRating.ts` in the app builds its body by hand with no
//     position in it — asserted by `tests/routeRating.test.ts`, which fails if
//     a coordinate ever appears. A free-text note is stored ONLY for a "bad"
//     verdict, on both sides, so the policy can say what it says.
//     It is NOT behind the Settings → Privacy switch, and that is deliberate
//     rather than an oversight: the switch governs passive usage counting, and
//     a rating is something the student deliberately submitted about a route
//     they just walked. The page describes it under "What the app sends" beside
//     problem reports, which is the thing it actually resembles.
//   · There IS a per-install identifier — `deviceIdFor` hashes the attestation
//     key — and pretending otherwise would be the dishonest kind of "we don't
//     use identifiers". It is described for what it is.
//   · The app checks for JS updates against Expo's servers on launch, which is
//     a third party contacted on every run and is invisible from the UI. It is
//     named.
//
// SCOPE. This is the APP's policy. The website is a different thing with a
// different answer — it runs page-view analytics and has an email sign-up on
// the landing page, neither of which exists in the app — so it gets one short
// section saying so rather than being folded in, which would make the two store
// questionnaires impossible to answer consistently.
//
// ⚠️ THE APP NOW COUNTS ITS OWN USAGE (2 September 2026). It posts route
// searches and the first-run answers to our own /api/path-log and
// /api/app-profile. This page said twice, categorically, that there were no
// analytics in the app; those sentences are gone, because a policy that is
// wrong about the one thing somebody came here to check is worse than no
// policy. Three things have to stay true of the wording, and are asserted by
// what the app actually does (src/analytics/ in UMapsMobile, and its tests):
// the switch is in Settings and stops every send immediately, the session id is
// never written to the phone's storage, and the install id — which exists so a
// corrected answer replaces the old one — is deleted by Reset preferences.
//
// ⚠️ RESTAURANT ACTIVITY (21 September 2026). The app now also posts what
// students do on the restaurant screens to /api/restaurant-log
// (api/_restaurantLog.js): which counter or dish was opened, what was typed
// into a food search, Directions, the hours. Same promise as the searches: the
// session id counts distinct visitors per day and is then DROPPED — no stored
// row carries it — and it sits behind the same Settings → Privacy switch. The
// one piece of free text is the search term, which is why the row says so.
//
// ⚠️ CLUB EVENT ACTIVITY (written up 11 October 2026; the app has posted it
// since the event-log endpoint existed). The app posts what students do with
// club events to /api/event-log (api/_eventLog.js): opened an event from its
// map pin or a list row, Directions, saved or unsaved it, opened its tickets,
// followed one of its links, opened a club's page. A stored row carries the
// interaction, the event's id, name and host club, for a link the LABEL the
// club wrote (never the URL), ios/android and the returning flag — no session
// id (it only counts distinct visitors per day in a 48-hour set), no position,
// no account. Behind the same Privacy switch as the other counts, so the
// "usage, and can be switched off" list below is four rows, not three.
//
// ⚠️ SHUTTLE REPORTS (29 September 2026). "I've gotten on the bus" posts to
// /api/shuttle-report the PUBLISHED coordinates of the stop the phone resolved
// itself to (UMapsMobile `ArrivalStrip`, `reportPrecheck`) — never the fix,
// whose `accuracy` alone travels — plus an `install` that is a random id made
// fresh each local day (`src/shuttle/reporter.ts`), never the analytics install
// id, and sent whether or not the Privacy switch is on (it is a report, not a
// count). The server stamps its own time, keeps the point with `by` — a hash of
// that id — for an hour after the newest report (50 at most), and logs which
// stop and when with no id at all (api/_shuttle.js). The Location section's
// "your phone's location itself is never transmitted" stays true only while
// that holds (and while the outdoor auto-start above sends a map node, never
// the fix).
//
// ⚠️ ADS (29 September 2026). The app now carries the Google Mobile Ads SDK
// (UMapsMobile `src/ads/admob.tsx`): a banner and at most four interstitials a
// visit, for phones without Trailblazer. Every request is non-personalised; the
// app never shows the ATT prompt and blocks Android's AD_ID permission, so the
// SDK has no advertising identifier on either platform — which is what keeps
// "no advertising identifier" in the short version true. Phones whose time zone
// or region is in the EEA, the UK or Switzerland get no ads and never start the
// SDK (`src/ads/region.ts`), which is what lets the app ship without a consent
// dialog. UMapsMobile's `npm run preflight` fails a release with ads on while
// this page still says there are none, or does not name AdMob.
//
// ⚠️ ANNOUNCEMENTS (24 October 2026). The app now fetches /api/popups on
// launch — the operator's notices and sponsored messages (docs/POPUPS.md), one
// small signed feed, re-checked with an ETag so an unchanged feed is a 304. The
// request carries nothing about the phone, which is why its row sits with the
// other plain requests. Seeing one is COUNTED to /api/popup-log
// (api/popup-log.js): the popup's id and whether it was shown, closed or
// tapped, with the per-launch session id that every other count carries and
// nothing else — no identifier is stored, the server keeps per-popup totals
// only (`popups:counts`). It sits behind the same Settings → Privacy switch, so
// the "usage, and can be switched off" list below is FIVE rows, not four.
//
// ⚠️ THE SAFETY BUTTON (3 November 2026) makes it SIX. Opening the Safety pill
// and tapping each of its four numbers is counted to /api/safety-log
// (api/safety-log.js) — and that body is ONLY the row's name: no session id,
// no platform, nothing. One of the four is the Klinic Crisis Line, so the
// server keeps a total per row per day and nothing that could join two taps.
// The page says so in words, because "we count taps on a crisis line" is the
// sentence somebody suspicious goes looking for.
//
// If any of that changes, this page changes in the SAME commit, and the App
// Store Connect and Play Console data-collection questionnaires change with it:
// they are answered from this text, and a store listing that disagrees with the
// linked policy is what an app review actually catches.
//
// WHY IT LOOKS LIKE NOTHING. Deliberately the plainest page on this site: black
// text on white, one column, no card, no gradient, no tinted callouts. A privacy
// policy is read in two situations — a store reviewer checking that a URL
// resolves to a real policy, and somebody who has become suspicious enough to go
// looking — and design is worth nothing in either. It is also the page most
// likely to be printed, pasted into a form, or read by a screen reader, all of
// which the decoration was in the way of. Anything left here is doing structural
// work: the measure, the headings, and the tables that become lists on a phone.
//
// Styled inline like every other page here; see UMapsWordmark.jsx for why.

import { useEffect } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';

// Update BOTH when the policy changes materially. The date is what tells a
// returning reader whether the thing they agreed to is the thing on screen.
const UPDATED = '24 October 2026';
const CONTACT = 'umapscontact@gmail.com';

export default function MobilePrivacyPage() {
  useEffect(() => {
    document.title = 'UMaps — Mobile App Privacy Policy';
  }, []);

  return (
    <div style={S.page}>
      <style>{STACK_ON_PHONE}</style>

      <header style={S.top}>
        <UMapsWordmark tool="Privacy" size={20} href="/umanitoba" />
      </header>

      <main style={S.card}>
        <h1 style={S.h1}>Privacy Policy — UMaps mobile app</h1>
        <div style={S.meta}>
          Last updated {UPDATED} · Applies to the UMaps app for iOS and Android
        </div>

        <P>
          UMaps is a campus map and pathfinder for the University of Manitoba, built and run
          as an independent student project in Winnipeg, Manitoba, Canada. It is{' '}
          <b>not affiliated with, endorsed by, or operated by the University of Manitoba</b>.
          In this policy, &ldquo;we&rdquo; and &ldquo;UMaps&rdquo; mean that project, and
          &ldquo;the app&rdquo; means the UMaps application for iOS and Android.
        </P>

        <P>
          This policy explains what the app does with information, what it sends to us, and
          what it never sends anywhere. It is written to match how the app actually behaves.
          Questions go to <a style={S.link} href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </P>

        {/* The summary was a tinted panel above the introduction. It is a
            section like every other one now: a heading is enough to find it,
            and a policy that shouts one paragraph at you reads as though the
            rest is the part you were not meant to look at. It sits after the
            two sentences that say what the app is, because "no accounts, no
            tracking" means nothing until you know whose app it is about. */}
        <Section title="The short version">
          <P>
            UMaps has no accounts and no login. Your timetable and your phone&rsquo;s location
            stay on your phone. Unless you have UMaps Trailblazer, the app shows ads from Google — never
            personalised, and never in the EEA, the UK or Switzerland (see <b>Ads</b> below).
            There is no third-party analytics or tracking software, and no advertising
            identifier. The app does send us anonymous counts of what gets used —
            which routes are searched for, and the answers to the optional first-run questions
            — and you can turn that off in <b>Settings → About → Help improve UMaps</b>. We do not sell or share
            your information with anyone.
          </P>
        </Section>

        <Section title="What stays on your phone">
          <P>
            None of the following is ever sent to us. All of it is removed when you uninstall
            the app, and most of it can be cleared from the app&rsquo;s Settings at any time.
          </P>
          <Table
            head={['What', 'Why the app keeps it']}
            rows={[
              ['Your timetable — the course reference numbers you added',
               'To draw your week and put today’s classes on the map'],
              ['Friends’ timetables you imported from a shared link',
               'To compare schedules side by side'],
              ['Your year, faculty and commuter preferences',
               'To decide which information the app leads with'],
              ['Accessibility and base-map preferences',
               'So you do not set them again every time'],
              ['Places you starred',
               'To keep them at the top of the Nearby list'],
              ['Star ratings you gave',
               'So rating the same place again corrects your first rating instead of adding a second'],
              ['Downloaded campus map data',
               'So the app works with no signal — which is exactly where you need it, three floors underground'],
            ]}
          />
          <P>
            <b>Settings → Preferences → Reset preferences</b> clears your answers.{' '}
            <b>Settings → Clear downloaded data</b> clears the cached map. Neither one touches
            the other.
          </P>
        </Section>

        <Section title="Location">
          <P>
            If you allow it, the app reads your location <b>only while it is open</b>, to show
            where you are on the map, to sort nearby places by how far away they are, to start
            directions from where you are standing when you are outside a building, and — if
            you report the campus shuttle — to work out which shuttle stop you are at.
          </P>
          <ul style={S.ul}>
            <li style={S.li}>
              <b>Your phone&rsquo;s location itself is never transmitted.</b> Routes are
              calculated on your phone, from map data already downloaded to it, and your
              coordinates never reach us or anyone else. The one thing that comes close: when
              you ask for directions while standing outside a building, the app starts the route
              at the nearest point on the campus footpath network, and — only if{' '}
              <b>Help improve UMaps</b> is on — the route search it counts names that footpath
              point. That is a fixed point on our campus map, not your coordinates.
            </li>
            <li style={S.li}>
              <b>There is no background location access.</b> The app does not request the
              background location permission on either platform, so it cannot read your
              position when it is not running.
            </li>
            <li style={S.li}>
              <b>The app never asks unprompted.</b> The permission dialog appears only when you
              use something that needs it, and declining leaves every other feature working.
            </li>
          </ul>
          <P>
            You can revoke location access at any time in your phone&rsquo;s own settings.
          </P>
        </Section>

        <Section title="Photos and camera">
          <P>
            If you choose to add a photo of a campus place that has none, the app asks for
            access to your photo library or camera so you can pick or take one. It reads
            nothing from your library for any other purpose, and does not browse it.
          </P>
          <P>
            A photo you submit is sent to us with the place it belongs to and nothing else. It
            is <b>reviewed before anyone else can see it</b>, and it is not linked to any
            identity — there is no account to link it to. To have one removed, email us with
            enough detail to find it.
          </P>
        </Section>

        <Section title="What the app sends to us">
          <P>
            The app talks to servers we operate at <code style={S.code}>myumaps.ca</code>. These
            are the only requests it makes to us, and none of them carries your name, your
            email, an account, or an advertising identifier.
          </P>
          <Table
            head={['When', 'What is sent']}
            rows={[
              ['Downloading the campus map',
               'Which buildings, floor plans, navigation data, places, parking lots and bus stops to send. Nothing about you.'],
              ['Opening a bus stop',
               'The stop number, so we can look up its departures. Winnipeg Transit’s own service is called from our server, not from your phone, so your device never contacts them.'],
              ['Opening the app',
               'A request for the current list of campus events.'],
              ['Opening the app',
               'A request for the current announcements — notices or sponsored messages the operator has published for the app to show.'],
              ['Showing the campus shuttle',
               'A request for where the shuttle is and when it reaches each stop, repeated every few seconds while the shuttle is on your map and the app is open. It carries nothing about you or where you are.'],
              ['Rating a place',
               'Which place, how many stars, and what you had rated it before — so the new rating replaces the old one rather than counting twice.'],
              ['Submitting a photo',
               'The image and which place it is of.'],
              ['Writing a restaurant review',
               'The dish, your star rating, what you wrote and any photos you added, which are checked before anyone else sees them. It carries the app’s random install identifier, so your reviews appear under one name: the reviewer name you chose, or “Anonymous” if you ticked that box — an anonymous review still carries the identifier, it just is not shown.'],
              ['Choosing a reviewer name',
               'The name you picked and the app’s random install identifier, so the name stays yours.'],
              ['Buying UMaps Trailblazer',
               'Nothing to us. The payment is handled entirely by Apple or Google, who never share your card or account details with us. RevenueCat, the service that confirms purchases for us, receives the store’s record of what you bought and a random identifier it creates for this install, so the app can unlock what you paid for and restore it on a new phone.'],
              ['Reporting a problem',
               'What you typed, plus which rooms you were routing between, which route options were on, and whether a route was found. Deliberately no location.'],
              ['Rating a route',
               'Whether you tapped Bad, Ok or Good, plus the two rooms, which route options were on, how far the route was and how many steps it had — and, only if you tapped Bad and chose to write one, what you said was wrong. Deliberately no location.'],
              ['Reporting the campus shuttle (“I’ve gotten on the bus”)',
               'The published location of the shuttle stop you are at — never your phone’s own location, which is used on your phone only to work out which stop that is — and how precise your phone’s location reading was, as a number of metres. We record the time it arrived. It carries a random identifier that the app makes fresh every day and that is not linked to anything else in this policy; it is used only so one phone cannot report the bus over and over, or count as two riders. If you answer the follow-up question, the app sends whether the bus arrived early, on time or late, with that same identifier and nothing else.'],
              ['Searching for a route (can be turned off)',
               'The two rooms, which route options were on, whether a route was found and how far it was — so we can see which buildings people are trying to reach and where the map fails them. No location — except that a route started from outside a building names the point on the campus footpath network it started from, never your coordinates.'],
              ['Using the restaurant screens (can be turned off)',
               'Which restaurant or menu item you opened, what you typed into a food search, and whether you asked for directions, checked the hours, opened a Weekly Highlight or opened an item’s reviews — so we can see which campus food people look for and show a restaurant how its page is used. No location, and nothing that says who you are.'],
              ['Using club events (can be turned off)',
               'Which club event you opened — from its pin on the map or from a list — and whether you asked for directions, saved it, opened its tickets, followed one of its links or opened the club’s page, along with the event’s name and club, whether you have used the app before, and whether your phone is an iPhone or Android. For a link, the label the club gave it, never the address. So we can see whether students find events in time, and tell a club how its event was found. No location, and nothing that says who you are.'],
              ['Seeing an announcement (can be turned off)',
               'Which announcement was shown, closed or tapped, so we know whether anybody read it.'],
              ['Using the Safety button (can be turned off)',
               'That the Safety button was opened, or which of its numbers was tapped — Campus Security, Safe Walk, the non-emergency police line or the Klinic Crisis Line — so we know whether these numbers are worth the space. Only a running total for each, per day: no session identifier, nothing about your phone, no location, and nothing that could connect one tap to another or to you. The call itself is made by your phone; we never see it.'],
              ['Finishing the first-run questions (can be turned off)',
               'Your year, faculty and which commuter overlays you kept — each of which you may skip — and how many courses you entered, never which ones.'],
            ]}
          />
          <P>
            The last six are the only things here that are about usage rather than about
            answering a request, and they are the six you can switch off, in{' '}
            <b>Settings → About → Help improve UMaps</b>. They are on by default. Turning the switch off stops
            every one of them immediately.
          </P>
          <P>
            They carry a <b>random session identifier</b> that lives only until you close the
            app and is never written to your phone&rsquo;s storage, so that a day&rsquo;s
            searches can be counted as one visit rather than several. For the restaurant screens
            and club events it is used only to count how many different people used them that
            day, and is <b>not stored with what you looked at</b>. The Safety button sends no
            identifier at all. The first-run answers
            instead carry a <b>random install identifier</b>, which is stored, and exists for
            one reason: if you change an answer later in Settings, it replaces your earlier
            answer instead of counting you as a second student. Neither is derived from your
            phone, from the attestation below, or from anything that survives a reinstall, and{' '}
            <b>Settings → Preferences → Reset preferences</b> deletes the stored one.
          </P>
          <P>
            Sharing the app uses a third one. When you first open the share screen the app
            makes a <b>random referral code</b> and stores it on your phone, and the link and
            QR code it gives you are{' '}
            <b>myumaps.ca/mobile/refer/&lt;code&gt;</b>. When somebody opens that link and taps
            through to the App Store or Google Play, we add one to that code&rsquo;s count
            (once per person per day), which is what lets the app tell you how many students
            you have helped. We store <b>only a number against the code</b>: nothing records
            who tapped it, nothing checks whether they went on to install the app, and nothing
            on our side says whose code it is. It is not derived from your phone or from any other identifier here, and{' '}
            <b>Settings → Preferences → Reset preferences</b> deletes it — which also resets
            your count to zero.
          </P>
          <P>
            One thing worth being plain about: a referral code is <b>not anonymous in every
            sense</b>. We cannot tell who owns one, but the link travels in messages you send
            under your own name — so anybody you send it to could, in principle, read
            &ldquo;this code has helped 12 students&rdquo; as being about you. That is a
            consequence of sharing a link at all rather than of anything we store, and you can
            simply not share it.
          </P>
          <P>
            Like any web request, our servers see your IP address and the fact that a request
            was made. This is used to keep the service running and to stop abuse, not to build
            a profile of you, and it is not combined with anything else in this policy.
          </P>
        </Section>

        <Section title="Proving the app is the app">
          <P>
            So that the campus map data cannot simply be scraped by anything that knows the
            URL, each install proves itself to our server using <b>Apple&rsquo;s App Attest</b>{' '}
            or <b>Google&rsquo;s Play Integrity</b>. These check that the request came from a
            genuine, unmodified copy of UMaps on a real device.
          </P>
          <P>
            To do this the app sends your platform, your operating system version, your device
            model (for example &ldquo;iPhone 14&rdquo;), and whether it is running on real
            hardware. From the attestation our server derives a <b>random per-install
            identifier</b>, which it uses to apply rate limits per device rather than per
            network — otherwise everyone sharing campus Wi-Fi would count as one user.
          </P>
          <P>
            That identifier is not linked to you, is not an advertising identifier, is not used
            for tracking, is not shared with anyone, and changes if you reinstall the app. We
            have no way to work backwards from it to a person.
          </P>
        </Section>

        <Section title="How long anything is kept">
          <Table
            head={['What', 'How long']}
            rows={[
              ['Problem reports', 'Up to two years, and only the most recent 5,000, after which the oldest are discarded automatically'],
              ['Route ratings', 'Up to two years, and only the most recent 20,000, after which the oldest are discarded automatically'],
              ['Shuttle reports', 'The stop, the time and a scrambled form of that day’s identifier: only the most recent 50, all discarded an hour after the newest one. A log of which stop and when, with no identifier, for up to 90 days and only the most recent 5,000'],
              ['Photo submissions', 'Until reviewed; approved photos stay on the map, rejected ones are removed. Either can be deleted on request'],
              ['Restaurant reviews and reviewer names', 'For as long as they are published. Any can be removed on request'],
              ['Star ratings', 'Kept as running totals for each place — a count and an average, with nothing identifying who voted'],
              ['Route searches', 'Up to a year, and only the most recent 5,000, after which the oldest are discarded automatically. The daily totals they are counted into are kept indefinitely, and are counts only'],
              ['Restaurant activity', 'The most recent 5,000 individual actions for up to a year, with no identifier on them, after which the oldest are discarded automatically. The daily totals they are counted into are kept indefinitely, and are counts only'],
              ['First-run answers', 'Kept as counts. The most recent 5,000 individual answers are also kept for up to a year; the identifiers that make a day’s visits countable expire within 48 hours'],
              ['Server request logs', 'Kept briefly by our hosting provider for operating the service, then discarded'],
              ['Everything on your phone', 'Until you clear it in Settings or uninstall the app'],
            ]}
          />
        </Section>

        <Section title="Ads">
          <P>
            If you do not have UMaps Trailblazer, the app shows a banner at the bottom of the
            map and, now and then, a full-screen ad — at most four in one visit, never within
            ninety seconds of each other, and never in your first thirty seconds. The ads come
            from Google AdMob, whose software is part of the app.
          </P>
          <P>
            <b>What UMaps tells Google: nothing about you.</b> Every ad is requested as
            non-personalised, so it is not chosen from a profile of you. UMaps passes Google no
            location, no timetable, none of your answers and no identifier of its own.
          </P>
          <P>
            <b>What Google&rsquo;s software receives anyway.</b> Like any ad, the request
            itself reaches Google: your IP address (from which a rough location such as the
            city can be estimated), your device model and operating system, the app, and an
            identifier that is scoped to this app&rsquo;s developer on your phone. Google uses
            these to show and count the ad, limit how often you see it, and detect fraud. The
            app does not let it read your phone&rsquo;s advertising identifier: on iPhone it
            never asks to track you, and on Android it is not given the permission. Google
            handles this under its own policy, at policies.google.com/technologies/partner-sites.
          </P>
          <P>
            Phones set to a time zone or region in the European Economic Area, the UK or
            Switzerland are shown no ads at all, and do not contact Google&rsquo;s ad servers.
          </P>
        </Section>

        <Section title="Other companies involved">
          <P>
            The app contains no third-party analytics: the usage counts described above go to
            our own servers and to nobody else&rsquo;s. Besides the ads described above, it
            relies on these services, each of which handles data under its own policy:
          </P>
          <Table
            head={['Service', 'What it does here']}
            rows={[
              ['Google Maps (Google LLC)',
               'Renders the map itself. Its SDK is part of the app, and Google receives what it needs to serve map imagery. See policies.google.com/privacy'],
              ['RevenueCat (RevenueCat, Inc.)',
               'Confirms UMaps Trailblazer purchases with Apple and Google and tells the app what is unlocked. It receives the store’s purchase record and a random identifier for this install — no name, email or payment details. See revenuecat.com/privacy'],
              ['Google AdMob (Google LLC)',
               'Serves the ads described above, to phones without Trailblazer outside the EEA, the UK and Switzerland. See policies.google.com/technologies/partner-sites'],
              ['Apple App Attest / Google Play Integrity',
               'Verifies the app is genuine, as described above'],
              ['Expo (EAS Update)',
               'On launch the app checks for a JavaScript update, sending its platform and build identifier. No personal information is included'],
              ['Vercel',
               'Hosts myumaps.ca and the API the app talks to, and therefore processes those requests on our behalf'],
            ]}
          />
          <P>
            Downloading the app also means Apple or Google know you did. That is between you
            and them; we receive only aggregate download counts with nothing identifying in
            them.
          </P>
        </Section>

        <Section title="Sharing your timetable">
          <P>
            When you share your timetable, the app builds a link containing your name as you
            typed it and the reference numbers of your courses. The link is created on your
            phone and is only ever sent by you, to whoever you choose. Nothing is uploaded, and
            we never see it.
          </P>
          <P>
            Timetable images you export are generated on your phone and go wherever you send
            them. Calendar files are the same.
          </P>
          <P>
            Anyone you send a link to can read the courses in it, so treat it the way you would
            treat any message about your schedule.
          </P>
        </Section>

        <Section title="Your choices and rights">
          <P>
            Under Canada&rsquo;s Personal Information Protection and Electronic Documents Act
            (PIPEDA), you can ask what personal information we hold about you, ask us to
            correct it, and ask us to delete it. Because UMaps has no accounts, in practice
            there is almost nothing to look up — but you can:
          </P>
          <ul style={S.ul}>
            <li style={S.li}>
              Turn location or photo access on or off at any time in your phone&rsquo;s settings
            </li>
            <li style={S.li}>
              Stop the app sending any usage counts with <b>Settings → About → Help improve UMaps</b>
            </li>
            <li style={S.li}>
              Clear your answers, and the stored install identifier with them, using{' '}
              <b>Settings → Preferences → Reset preferences</b>
            </li>
            <li style={S.li}>
              Clear the downloaded map with <b>Settings → Clear downloaded data</b>
            </li>
            <li style={S.li}>
              Remove everything by uninstalling the app
            </li>
            <li style={S.li}>
              Ask us to delete a photo, a problem report or a route rating you sent, by emailing{' '}
              <a style={S.link} href={`mailto:${CONTACT}`}>{CONTACT}</a> with enough detail to
              identify it — since there are no accounts, we cannot look it up by you
            </li>
          </ul>
          <P>
            If you are not satisfied with how we handle a request, you can complain to the
            Office of the Privacy Commissioner of Canada at{' '}
            <a style={S.link} href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">
              priv.gc.ca
            </a>.
          </P>
        </Section>

        <Section title="Children">
          <P>
            UMaps is intended for university students, staff and visitors. It is not directed
            at children, does not ask for anyone&rsquo;s age, and knowingly collects no
            information from children.
          </P>
        </Section>

        <Section title="The UMaps website is separate">
          <P>
            This policy covers the mobile app. The website at{' '}
            <code style={S.code}>myumaps.ca</code> does two things the app does not:
          </P>
          <ul style={S.ul}>
            <li style={S.li}>
              Its landing page offers an optional sign-up where you can enter your{' '}
              <code style={S.code}>@myumanitoba.ca</code> or{' '}
              <code style={S.code}>@umanitoba.ca</code> email to support the project. Nothing is
              recorded unless you type an address and submit it. The app never asks for an
              email.
            </li>
            <li style={S.li}>
              It counts page views, without cookies, to see which tools get used. The app
              counts its own usage separately and only as described above, and unlike the
              website it lets you switch that off.
            </li>
          </ul>
        </Section>

        <Section title="Changes to this policy">
          <P>
            If this policy changes in a way that matters, the new version is posted at this
            address with a new date at the top, and — where the change is significant — noted
            in the app&rsquo;s release notes. Continuing to use UMaps after a change means you
            accept the updated policy.
          </P>
        </Section>

        <Section title="Contact">
          <P>
            Questions about this policy, or about anything the app does with information:
          </P>
          <p style={S.contact}>
            <a style={S.link} href={`mailto:${CONTACT}`}>{CONTACT}</a>
          </p>
        </Section>

        <div style={S.foot}>
          <a style={S.footLink} href="/umanitoba">← Open the UMaps map</a>
        </div>
      </main>
    </div>
  );
}

// ── Small pieces, local because nothing else on the site needs them ──────────

function Section({ title, children }) {
  return (
    <section style={S.section}>
      <h2 style={S.h2}>{title}</h2>
      {children}
    </section>
  );
}

function P({ children }) {
  return <p style={S.p}>{children}</p>;
}

/**
 * A two-column table that becomes two stacked lines on a phone.
 *
 * Most of this page is read on the device it is about, and a real <table> with
 * prose in both columns is unreadable at 360px — so the same rows stack below
 * the breakpoint. Done with a media query in a <style> tag rather than a resize
 * listener: it has to be right on the first paint, and a store reviewer's
 * screenshot is the first paint.
 *
 * THE ONE THING ON THIS PAGE THAT IS NOT STYLED INLINE, and it has to be. An
 * inline style beats a stylesheet rule regardless of the media query, so
 * `style={{ width: '38%' }}` on a cell survives into the stacked layout and
 * leaves the key column 38% wide in a block flow — and a `padding` set inline
 * cannot be collapsed either. Every table rule therefore lives in the sheet,
 * where the wide and narrow versions can actually override one another.
 */
function Table({ head, rows }) {
  return (
    <table className="umaps-priv-table">
      <thead>
        <tr>
          {head.map((h) => <th key={h}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b]) => (
          <tr key={a}>
            <td className="k">{a}</td>
            <td>{b}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Every rule the tables need, wide and narrow.
 *
 * Below 560px they stop being tables: both columns hold prose, and two prose
 * columns in 360px are four words wide each — so each row becomes its own block
 * with the column heading printed above the cell it labels, and the real
 * <thead> is hidden.
 *
 * A media query rather than a resize listener because it must be right on the
 * FIRST paint: this page is opened by store reviewers on a phone, and a layout
 * that corrects itself after a listener fires has already been screenshotted
 * wrong.
 */
const STACK_ON_PHONE = `
.umaps-priv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  margin: 4px 0 14px;
}
.umaps-priv-table th {
  text-align: left;
  padding: 6px 14px 6px 0;
  border-bottom: 1px solid #999;
  font-weight: 700;
}
.umaps-priv-table td {
  padding: 9px 14px 9px 0;
  border-bottom: 1px solid #e4e4e4;
  line-height: 1.55;
  vertical-align: top;
}
.umaps-priv-table td.k { width: 38%; }

@media (max-width: 560px) {
  .umaps-priv-table, .umaps-priv-table tbody, .umaps-priv-table tr, .umaps-priv-table td {
    display: block; width: auto;
  }
  .umaps-priv-table thead { display: none; }
  .umaps-priv-table tr { padding: 10px 0; border-bottom: 1px solid #e4e4e4; }
  /* "td.k" has to be named again here: the 38% above is set on td.k, which
     out-specifies a bare td and would otherwise survive into the stacked
     layout and leave the key column a third of the screen wide.
     (No backticks in this comment — the whole sheet is a template literal.) */
  .umaps-priv-table td,
  .umaps-priv-table td.k { width: auto; border-bottom: none; padding: 0 0 4px; }
  /* The first cell in bold and the second under it, with no repeated column
     headings. Every table here pairs a thing with a sentence about that thing
     — "Problem reports" / "Up to two years" — so the pairing survives the
     stacking on its own, and printing "What:" before each of eighteen rows was
     the loudest thing left on a page meant to be quiet. */
  .umaps-priv-table td.k { font-weight: 700; }
}
`;

// One column of black text on white. The only numbers that matter here are the
// measure (~70 characters, which is what makes a page of prose readable at all)
// and the space between a heading and the paragraph under it.
const S = {
  page: {
    background: '#fff',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    color: '#111',
    padding: '0 20px 64px',
  },
  // A line, not a banner: this says which site you are on and gets out of the
  // way. The rule under it is the only ornament left on the page, and it earns
  // its place by separating the site from the document.
  top: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '20px 0 14px',
    borderBottom: '1px solid #ddd',
  },
  card: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '26px 0 0',
  },
  h1: { fontSize: 24, lineHeight: 1.25, margin: '0 0 6px', fontWeight: 700 },
  meta: { fontSize: 14, color: '#555', marginBottom: 8 },
  section: { marginTop: 28 },
  h2: { fontSize: 18, margin: '0 0 8px', fontWeight: 700 },
  p: { fontSize: 16, lineHeight: 1.6, margin: '0 0 12px' },
  ul: { margin: '0 0 12px', paddingLeft: 22 },
  li: { fontSize: 16, lineHeight: 1.6, marginBottom: 8 },
  // Links stay blue and underlined. That is not decoration — it is the one
  // piece of styling a reader has to be able to see, and this page has an email
  // address and a regulator's website in it that people are meant to use.
  link: { color: '#0b57d0', textDecoration: 'underline', wordBreak: 'break-word' },
  code: { fontFamily: 'ui-monospace,Menlo,Consolas,monospace', fontSize: '0.94em' },
  contact: { fontSize: 16, margin: '0 0 12px' },
  foot: { marginTop: 32, paddingTop: 16, borderTop: '1px solid #ddd' },
  footLink: { color: '#0b57d0', textDecoration: 'underline', fontSize: 15 },
};
